/**
 * Campaign Mode — Narrative Text
 *
 * All story beats, rival dialogues, zone descriptions, world taglines, and the
 * campaign intro live here. Edit this file to improve, rewrite, or localise the
 * narrative without touching any game-mechanics logic in campaignConfig.ts.
 *
 * ----------------------------------------------------------------------------
 * THE SAGA — "The Last Souls" (read this before editing any line)
 * Spec: docs/superpowers/specs/2026-07-06-campaign-story-bible-the-last-souls.md
 * ----------------------------------------------------------------------------
 * The worlds are not unrelated tales. They are chapters of ONE story, and every
 * line should serve it:
 *
 *   Long ago the island of Suikara had a heart, the SOUL OF SUIKARA, that kept
 *   everything in balance. It cracked, and its 19 pieces hid inside 19 different
 *   kinds of Suikamon; a piece in a Suikamon's heart is a SOUL SHARD, and its
 *   carriers are the 19 LAST SOULS, waiting at the campaign's 19 soul nodes.
 *   Without them the island slips out of balance, one Tale's trouble at a time.
 *   Bring all 19 home and Suikara is whole; miss one and it never heals. Clan Z
 *   cage the carriers; the SOUL-EATER, who eats names, walks the island too.
 *   WHY it cracked is the saga's secret: the island forgot its First Keeper
 *   (Tale 6). (A Wishing Star, 2026-10-04 to 2026-10-11, and a broken "Soul"
 *   before that; see the bible's prologue for why it changed.)
 *
 *   TWO LAWS run the whole saga:
 *     1. Battles get you to the door; kindness opens it. Every finale is
 *        decided by a specific character's act of compassion — never by power.
 *     2. Forgetting corrodes whoever fights it. Carrying shards through
 *        ground the Soul-Eater has fed on erodes the bearer: words, then
 *        faces, then friends' names, then why they're fighting at all.
 *
 *   THE RACE: Old Man Gray walks the same map, starving, a thousand years
 *   ahead. A node he reaches first is GUTTERING — its trial twisted, its
 *   flame dim — and every loss there flickers it lower. A shard rescued after
 *   defeats sits dim until a clean rematch rebrightens it: it is never too
 *   late to be remembered properly.
 *
 *   THE RECURRING CAST travels the whole saga with you: Timmy the Youngster
 *   (baby Chirpy), Mimi & Lulu (twin Lasses), Vera the Channeler (hooded, unmasked in Act II),
 *   Cliff the Bird Keeper (Sentry, his hawk, rides his shoulder; Thistle, his Ray, fights for him), and Aggie the Medium,
 *   who gives you the Shard Book in World 1 and keeps your own name "for
 *   safekeeping" — you are "Keeper" until the epilogue.
 *
 *   The saga runs in TWO ACTS of five worlds each (authored levels 1–100 — the
 *   BANDS, not what a fight runs at: COMBAT_MAX_LEVEL caps every mode at 20),
 *   with the running Shard Book total spoken after every world: 4 · 7 · 8 · 9 · 11 ·
 *   13 · 14 · 15 · 17 · 19.
 *     • ACT I — THE FIVE WARDENS (Worlds 1–5, levels 1–50): five Guardians
 *       remembered, the cast assembled, ending on the reveal that the
 *       Soul-Eater's mirror wears the player's own face.
 *     • ACT II — THE FIRST KEEPER (Worlds 6–10, levels 51–100): the Champion's
 *       Road (World 6) reveals the Soul-Eater IS the First Keeper, forgotten
 *       when the last Keepers died. Pursue him for the four FIRST-SOUL shards
 *       until his hunger ends at the First Shore (World 10) — redeemed, not
 *       slain, his name written back into his own once-empty book.
 *
 *   FUTURE-SPECIES DEVICE: nineteen is not every kind the island ever had —
 *   it's what survived. Every "new" species that ever launches is canonically
 *   a species the Island's Soul remembers back, one at a time, over years.
 *
 *   NO GAMEPLAY CHANGE: this is a content-only recast — species, levels, XP,
 *   node ids, and soulSpeciesId never move; only names and prose do.
 *
 * RECURRING MOTIFS (keep these consistent across worlds):
 *   • "forgot its <name / keeper / purpose>" — every Guardian's wound.
 *   • emotion misread as a monster (grief→hunger, loneliness→a rising tide).
 *   • the player as the new KEEPER; victory = remembrance, not destruction.
 *   • a SOUL SHARD entrusted at each soul node (world finale / featured
 *     mini-boss — the 19 nodes with soulSpeciesId). Souls are never taken;
 *     they choose their Keeper.
 *   • cross-world echoes (the greened forest, the turned tide, embers in the
 *     heights) — phrased as island-wide rumor so they read naturally even
 *     though worlds now unlock in a fixed sequential order.
 *
 * STRUCTURE:
 *   NODE_STORIES  — per-node story beat + rival dialogue (keyed by nodeId)
 *   ZONE_STORIES  — per-zone chapter text (keyed by zoneId)
 *   WORLD_STORIES — per-world tagline (keyed by worldId, including locked worlds)
 *   CAMPAIGN_STORY_INTRO — opening text shown on the campaign map
 */

// ============================================================================
// Per-world narrative fragments (Worlds 3–10), merged into the maps below.
// World 1 & 2 text stays inline here; everything from Cinder Pass onward lives
// in ./campaign/stories/worldN.ts to keep this file within the size limit.
// (These import `type NodeStory` from this file — a type-only, runtime-erased
// import, so there is no module cycle.)
// ============================================================================

import { WORLD3_NODE_STORIES, WORLD3_ZONE_STORIES, WORLD3_TAGLINE } from './campaign/stories/world3.js';
import { WORLD4_NODE_STORIES, WORLD4_ZONE_STORIES, WORLD4_TAGLINE } from './campaign/stories/world4.js';
import { WORLD5_NODE_STORIES, WORLD5_ZONE_STORIES, WORLD5_TAGLINE } from './campaign/stories/world5.js';
import { WORLD6_NODE_STORIES, WORLD6_ZONE_STORIES, WORLD6_TAGLINE } from './campaign/stories/world6.js';
import { WORLD7_NODE_STORIES, WORLD7_ZONE_STORIES, WORLD7_TAGLINE } from './campaign/stories/world7.js';
import { WORLD8_NODE_STORIES, WORLD8_ZONE_STORIES, WORLD8_TAGLINE } from './campaign/stories/world8.js';
import { WORLD9_NODE_STORIES, WORLD9_ZONE_STORIES, WORLD9_TAGLINE } from './campaign/stories/world9.js';
import { WORLD10_NODE_STORIES, WORLD10_ZONE_STORIES, WORLD10_TAGLINE } from './campaign/stories/world10.js';
import { BOOK_1, BOOK_1_ZONES } from './campaign/books/index.js';

// ============================================================================
// Shared types
// ============================================================================

import type { NodeDialogue } from './campaign/rivalMemory.js';

export interface NodeStory {
  /** One-line story beat shown before the rival speaks. */
  story: string;
  /**
   * The rival's lines. intro/playerWin/playerLose are required; the optional
   * rematch/repeat variants (NodeDialogue) are selected from the player's
   * battle history with this rival and fall back to the base lines — so
   * variants can be authored world by world (World 1 first).
   */
  dialogue: NodeDialogue;
}

// ============================================================================
// Campaign opening text (shown on the map page)
// ============================================================================

/**
 * One screen of the cinematic intro. Each slide is a small run of paragraphs
 * revealed together; `**double asterisks**` mark a phrase to emphasise (the
 * reader splits on them — no markdown engine needed).
 */
export interface CampaignIntroSlide {
  paragraphs: string[];
}

/**
 * The campaign's opening cinematic. Shown once in a paged pop-up the first time
 * a Trainer opens Campaign, and re-openable from the story (ℹ) button. Keep each
 * line short; this is read one beat at a time, revealed with a staggered fade.
 * Editorial rules: open on an event, give a clear next goal, explain only the
 * lore needed now, distinguish character voices, and earn each line with a
 * clue, a reaction or a joke. Save revelations for their encounters. Rematches
 * must make sense after any number of attempts; losses must remain losses.
 */
export const CAMPAIGN_STORY_INTRO_SLIDES: CampaignIntroSlide[] = [
  {
    paragraphs: [
      'Come close, love. This is **Suikara**, and this is how Gran Aggie tells it.',
      'Long ago, the island had a Soul: a great shining crystal on its oldest hearth, the **Soul of Suikara**.',
      'Then it broke into **19** pieces, the **Soul Shards**. Each one hid inside just ONE Suikamon, the bravest of its kind: its **Last Soul**.',
      'Suikara is full of Gorlys. Only one of them carries the Gorly shard.',
    ],
  },
  {
    paragraphs: [
      'Without its Soul, gray dust spreads. Berry bushes go bare. The sea climbs.',
      'The legend says: bring all 19 home and Suikara is whole again. Miss one, and it never heals. **Clan Z** catch shard carriers and lock them in cages.',
    ],
  },
  {
    paragraphs: [
      'You grew up at Gran Aggie\'s fire, hearing that story. This morning, gray dust covered her garden.',
      'Your first Suikamon has been with you since the night you were born. It carries a secret.',
    ],
  },
];

/**
 * Flattened, emphasis-free version of the intro — used wherever a single plain
 * paragraph is needed (SEO copy, fallbacks). Derived from the slides so the two
 * never drift apart.
 */
export const CAMPAIGN_STORY_INTRO = CAMPAIGN_STORY_INTRO_SLIDES.flatMap(
  (slide) => slide.paragraphs,
)
  .join(' ')
  .replace(/\*\*/g, '');

// ============================================================================
// World taglines (keyed by worldId)
// ============================================================================

export const WORLD_STORIES: Record<string, string> = {
  // --- World 1: Verdant Forest (Nature) ---
  w1: BOOK_1.tagline,

  // --- World 2: Sunken Bay (Water) ---
  w2: 'A giant bell under the water keeps ringing, and every ring makes the tide rise. Find out who is ringing it before the last path floods.',

  // --- Act I, Worlds 3–6 (taglines authored in ./campaign/stories/worldN.ts) ---
  w3: WORLD3_TAGLINE,
  w4: WORLD4_TAGLINE,
  w5: WORLD5_TAGLINE,
  w6: WORLD6_TAGLINE,

  // --- Act II, Worlds 7–10 ---
  w7: WORLD7_TAGLINE,
  w8: WORLD8_TAGLINE,
  w9: WORLD9_TAGLINE,
  w10: WORLD10_TAGLINE,
};

/**
 * One-sentence teasers (keyed by worldId) for compact surfaces like the campaign
 * storyline overview, where the full multi-sentence WORLD_STORIES tagline is too
 * long. Keep each to a SINGLE evocative sentence.
 */
export const WORLD_TAGLINES_SHORT: Record<string, string> = {
  w1: 'A purple egg, a cold gray mist, and a gate that will not open.',
  w2: 'Find the bell under the bay before the tide covers the road.',
  w3: 'Climb a burning pass to a dragon guarding an abandoned forge.',
  w4: 'Reach the Guardian who keeps fighting the thunder.',
  w5: 'Meet your reflection, then hope it plays fair.',
  w6: 'Walk the old champions\' road and ask the questions Aggie has avoided.',
  w7: 'Follow the smoke to the hurt spirit Clan Z left behind.',
  w8: 'Open a vault full of storms that someone locked away.',
  w9: 'Stay close to your friends on a coast where gray dust buries every footprint.',
  w10: 'Take your team to the place where the whole story began.',
};

// ============================================================================
// Zone (chapter) descriptions (keyed by zoneId)
// ============================================================================

export const ZONE_STORIES: Record<string, string> = {
  // --- World 1 zones ---
  ...Object.fromEntries(BOOK_1_ZONES.map((z) => [z.zoneId, z.story])),

  // --- World 2 zones ---
  z4: 'The crossing floods a little more with every ring of the bell. Mimi found a cheap ferry. Zip found another way to block your path.',
  z5: 'Follow the bell into deeper water. A lantern glows above the sunken cages ahead.',

  // --- Worlds 3–10 zones (authored in ./campaign/stories/worldN.ts) ---
  ...WORLD3_ZONE_STORIES,
  ...WORLD4_ZONE_STORIES,
  ...WORLD5_ZONE_STORIES,
  ...WORLD6_ZONE_STORIES,
  ...WORLD7_ZONE_STORIES,
  ...WORLD8_ZONE_STORIES,
  ...WORLD9_ZONE_STORIES,
  ...WORLD10_ZONE_STORIES,
};

// ============================================================================
// Node stories and rival dialogues (keyed by nodeId)
// ============================================================================

export const NODE_STORIES: Record<string, NodeStory> = {

  // World 1 (Book One) is built from story/book-1-the-purple-egg.md: shared/domain/campaign/books/.

  // ==========================================================================
  // World 2, Chapter 1 — Tide Crossing
  // A bell beneath the bay has begun to toll, and every toll raises the tide.
  // ==========================================================================

  "z4-n1": {
    story: "On the ferry, Mimi waves one ticket at you. Behind her, Lulu tucks away their shared biscuit wrapper.",
    dialogue: {
      intro: "Keeper! The ferryman thought we were one girl walking fast! One fare, one biscuit! Bet you can't beat me!",
      playerWin: "No fair! But I already won today. One fare!",
      playerLose: "One fare, one biscuit, one win! Best day!",
      introRematchAfterLoss: "Back at the crossing! Lulu says you'll win. Traitor sister!",
      introRematchAfterWin: "Rematch by the sea! Salt air makes me stronger. That's science!",
      playerWinRepeat: "Again?! Fine. You buy the next biscuit. Both halves.",
      playerLoseRepeat: "Another win by the sea! I should have a boat named after me.",
    },
  },

  "z4-n2": {
    story: "Lulu bursts up from the water beside you, gasping. She points down, toward the bell.",
    dialogue: {
      intro: "I found a way past the reef. Beat me and I'll show you. Don't tell Mimi I got here first.",
      playerWin: "You're ready. Follow the bell. It gets louder past the reef.",
      playerLose: "The deep doesn't care how brave you are.",
      introRematchAfterLoss: "You're back. The bell still rings. Still lonely.",
      introRematchAfterWin: "You. I dive deeper now. Keep up.",
      playerWinRepeat: "You win. Follow close. The current's changed since last time.",
      playerLoseRepeat: "Still my reef. The bell can wait.",
    },
  },

  "z4-n3": {
    story: "A lantern glows under the flooded crossing. As you wade closer, something big moves beside it.",
    dialogue: {
      intro: "A wild Yeti bursts out of the tide! It blocks the crossing!",
      playerWin: "The Yeti dives deep and is gone.",
      playerLose: "The Yeti splashes you back off the stone path.",
      introRematchAfterLoss: "The wild Yeti surfaces again, blocking the crossing.",
      introRematchAfterWin: "The Yeti knows your smell now. It pops up, careful.",
      playerWinRepeat: "The Yeti slips back beneath the tide.",
      playerLoseRepeat: "The Yeti holds the crossing. The water rises.",
    },
  },

  "z4-n4": {
    story: "You round the reef, and there's Zip, blocking another path. Behind him, Dash puffs up to fill it edge to edge.",
    dialogue: {
      intro: "You again? Dash grew, see? So did Zap's Shade! Reef toll, pay up!",
      playerIntro: "Here's my toll. One battle.",
      playerWin: "Fine, no toll! Run, Dash! I'll block the pier instead!",
      playerLose: "Ha! Told you Clan Z was quicker!",
      introRematchAfterLoss: "Back on my reef? Dash is warmed up now.",
      introRematchAfterWin: "Dash says we can win this time. Well, he barked. Same thing.",
      playerWinRepeat: "Fine! The reef's yours. For now!",
      playerLoseRepeat: "Slow again! Clan Z owns the tide!",
    },
  },

  "z4-nw1": {
    story: "Something circles the shallows ahead of you, splashing anyone who comes near.",
    dialogue: {
      intro: "A wild Aqua pops up in the shallows! It wants this crossing to itself!",
      playerWin: "The wild Aqua flicks its tail and swims off.",
      playerLose: "The wild Aqua splashes you back to shore.",
      introRematchAfterLoss: "The shallows churn. The wild Aqua is back!",
      introRematchAfterWin: "The Aqua knows your team now. Its fins flare!",
      playerWinRepeat: "The wild Aqua dives into the bay.",
      playerLoseRepeat: "The shallows stay wild. The tide runs high.",
    },
  },

  "z4-n5": {
    story: "From the cliff, you hear Aggie counting the bell tolls out loud.",
    dialogue: {
      intro: "Hear how the bell speeds up? Match that pace, child. Tempest will show you.",
      playerWin: "Tempest has chosen you. Take its Soul Shard: that's five. The bell is farther down, in Drowned Deep.",
      playerLose: "Not yet, child. The deep needs a steadier team. Rest, then try again.",
      introRematchAfterLoss: "Back at my cliff? Good. The bell hasn't stopped. Neither should you.",
      introRematchAfterWin: "Keeper! The tide eases where you walk. Keep it up.",
      playerWinRepeat: "Tempest remembers you. The bell's still calling. Follow the sound.",
      playerLoseRepeat: "Tempest caught you again. Tea first, then another try.",
    },
  },

  "z4-n6": {
    story: "Cliff wades out of the current toward you, a soaked stray in his arms.",
    dialogue: {
      intro: "I've spent all night hauling strays out of the current. Show me you can stand in it.",
      playerWin: "Sentry! We lost! Nicely done. Find whoever's ringing that bell. We'll keep watch here.",
      playerLose: "The current caught you. Steady your team before you attack.",
      introRematchAfterLoss: "You're back. Good. Keep your footing this time.",
      introRematchAfterWin: "You held fast before. Sentry flies over water now. Reluctantly.",
      playerWinRepeat: "Still standing. Fewer strays in the current when you're here.",
      playerLoseRepeat: "We win. Sentry will gloat once she's dried off.",
    },
  },

  "z4-n7": {
    story: "At the top of the sunken steps, Mimi blocks your way. For once, she isn't joking.",
    dialogue: {
      intro: "Lulu is already down there. Nobody follows my sister without beating me!",
      playerWin: "No fair! ...Okay. Take care of her down there.",
      playerLose: "The way stays shut! Sisters first!",
      introRematchAfterLoss: "Still want down? Lulu said you'd be back. She's always right.",
      introRematchAfterWin: "Lulu says she can look after herself. I know. I'm still going to test you.",
      playerWinRepeat: "You keep winning, and I keep trusting you with my sister.",
      playerLoseRepeat: "The way stays shut for now. Come back after a rest.",
    },
  },

  // ==========================================================================
  // World 2, Chapter 2 — Drowned Deep
  // Player pets capped; the deep outlevels you on purpose.
  // ==========================================================================

  "z5-n1": {
    story: "Fresh flowers lie on the buoy. Lulu is staring at them when you row up.",
    dialogue: {
      intro: "Gray left flowers on the buoy. Then he just sat there. A battle might help me think.",
      playerWin: "He looked sad. That's all I know. Keep the flowers where they are.",
      playerLose: "You lost. I still haven't figured out the flowers.",
      introRematchAfterLoss: "You're back. The flowers are still there. So am I.",
      introRematchAfterWin: "You. I think better when I fight. Again.",
      playerWinRepeat: "I keep coming back to those flowers. He brought them for someone.",
      playerLoseRepeat: "Let's rest. I'm no closer to figuring him out.",
    },
  },

  "z5-n2": {
    story: "From Timmy's little boat, you both see it: something enormous passing underneath.",
    dialogue: {
      intro: "Something BIG keeps passing under my boat! Beat me before I panic!",
      playerWin: "I lost! That big thing never hunts. It just looks.",
      playerLose: "Ha! Even scared, I win! That's SO brave!",
      introRematchAfterLoss: "Back on my boat! The shadow missed you. Probably!",
      introRematchAfterWin: "The Keeper! I practiced in the dark. Chirpy hated it!",
      playerWinRepeat: "You're the brave one. I'm the LOUD one!",
      playerLoseRepeat: "Seven dark-water wins! Or two. Write it down somewhere waterproof!",
    },
  },

  "z5-n3": {
    story: "The drowned lighthouse looms over you. Zap guards it, and Shade circles the cages below.",
    dialogue: {
      intro: "Shade grew. His shadow covers every cage. Stay back.",
      playerIntro: "Then open them!",
      playerWin: "The latch broke. They all swam free. Zeck won't like this.",
      playerLose: "Quiet wins again. The cages stay shut.",
      introRematchAfterLoss: "Back at the wreck. Shade watched you. Ready.",
      introRematchAfterWin: "I've studied your team. Shade, let's see if it helped.",
      playerWinRepeat: "...Another cage sinks. Empty.",
      playerLoseRepeat: "Clan Z holds the wreck. Swim home.",
    },
  },

  "z5-n4": {
    story: "You find Aggie beside the bell, listening hard, her face sad.",
    dialogue: {
      intro: "People keep trying to smash the bell. It's calling for a missing Keeper, child. Let's prepare a better answer.",
      playerWin: "Ring back once. Let Tidebell, the bell's old Fernail, know someone heard. Then stay.",
      playerLose: "Not quite. Rest your team before you head for the bell.",
      introRematchAfterLoss: "Back to the bell? It rang a hundred years. It can wait for you.",
      introRematchAfterWin: "Back for practice? Good. I was getting tired of talking over that bell.",
      playerWinRepeat: "You've got it. Remember: ring back, then listen.",
      playerLoseRepeat: "Let's sit and listen for a moment. We can battle again after.",
    },
  },

  "z5-n5": {
    story: "Vera climbs out of the water in front of you, dripping from her hood.",
    dialogue: {
      intro: "I tried to stop that bell by force. The spirits laughed at me. Try the gentle way.",
      playerWin: "Maelstrom gives you its shard. That's six! Tidebell is just below. Remember what Aggie said.",
      playerLose: "Back to the surface. Breathe. We'll try again when you're ready.",
      introRematchAfterLoss: "Back for another dive? Take a proper breath this time.",
      introRematchAfterWin: "You again. The deep remembers you kindly. So do I.",
      playerWinRepeat: "The spirits went quiet again. Go gently with Tidebell.",
      playerLoseRepeat: "Up for air. A little slower next time. The bell isn't going anywhere.",
    },
  },

  "z5-n6": {
    story: "You race the tide to the bell! Tidebell the Fernail sends a wave, and it pulls Lulu toward the deep.",
    dialogue: {
      intro: "Tidebell lashes out as you reach the chamber. You'll have to calm it before anyone can answer the bell.",
      playerWin: "The twins ring the bell back, then tell stories until dawn. At last, Tidebell falls asleep. Its shard makes seven! 'We should charge for bedtime stories,' Mimi whispers. Lulu shushes her.",
      playerLose: "A wave sweeps you from the chamber. The bell keeps ringing. Regroup above the reef.",
      introRematchAfterLoss: "Tidebell rises again, then slows. It remembers the one who tried to answer.",
      introRematchAfterWin: "Tidebell greets you with a clear ring. It still enjoys a good match.",
      playerWinRepeat: "Tidebell bows beneath the quiet bell. Its shard glows brighter in your Shard Book.",
      playerLoseRepeat: "The bell wins this time, but gently. One clear note follows you up. Come back.",
    },
  },

  // --- Worlds 3–10 nodes (authored in ./campaign/stories/worldN.ts) ---
  ...WORLD3_NODE_STORIES,
  ...WORLD4_NODE_STORIES,
  ...WORLD5_NODE_STORIES,
  ...WORLD6_NODE_STORIES,
  ...WORLD7_NODE_STORIES,
  ...WORLD8_NODE_STORIES,
  ...WORLD9_NODE_STORIES,
  ...WORLD10_NODE_STORIES,
};
